import type { UserActor } from "@corpuscomplete/core/actor";
import {
  type Transaction,
  type ExpressionBuilder,
  type QueryRowType,
  Query,
  Row,
} from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Context, type Schema } from "../../zero/schema";
import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import { acquireAdvisoryLockIfServer } from "../../drizzle/advisory-lock";
import { assertValidIdChanges } from "../../utils/id-change";
import {
  mealPlan,
  plannedMeal,
  plannedMealItem,
  plannedMealItemDescriptor,
  plannedMealOccurrence,
  plannedMealSlot,
} from "./meal-plan.sql";
import { createInsertSchema, createUpdateSchema } from "../../utils/sql";
import { accessDenied, must } from "@corpuscomplete/core/utils";
import { Food } from "../food";
import { FoodItemMatcher } from "../food-matcher";
import { Nutrient } from "../nutrient";
import { DateRange, TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { temporalPlainDateSchema, temporalPlainTimeSchema } from "../../common/date-time/zod";
// Should be type-only import to avoid circular dependency
import type { FoodLog } from "../food-log";

/**
 * Meal-plan namespace boundary for collaborative planning and schedule mutation.
 *
 * Invariants:
 * - Visibility is restricted to owners and explicit plan members.
 * - Mutations validate membership, food visibility, and slot/meal references.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace MealPlan {
  export const createFormSchema = z.object({
    dateRange: DateRange.closedSchema,
  });
  export type CreateForm = z.infer<typeof createFormSchema>;

  const DEFAULT_MEAL_PLAN_DURATION_DAYS = 6;
  export function formDefault(today: Temporal.PlainDate): CreateForm {
    return {
      dateRange: {
        dateStart: today,
        dateEnd: today.add({ days: DEFAULT_MEAL_PLAN_DURATION_DAYS }),
      },
    };
  }

  export function formToCreate(input: {
    id: string;
    ownerId: string;
    form: CreateForm;
  }): MealPlan.Create {
    return {
      id: input.id,
      ownerId: input.ownerId,
      dateRange: input.form.dateRange,
      slots: [],
    };
  }

  const withRelated = (q: Query<"mealPlan">, userId?: string) => {
    return q.related("slots", (q) =>
      q
        .related("userNutrientTargets", (q) => (userId ? q.where("userId", "=", userId) : q))
        .related("meals", (q) =>
          q
            .related("items", (q) =>
              q
                .related("serving", (serving) => serving.related("food", (q) => q.related("meta")))
                .orderBy("sortIndex", "asc"),
            )
            .related("itemDescriptors", (q) => q.orderBy("sortIndex", "asc"))
            .related("occurrences", (q) => q.related("foodLogSourceGroup"))
            .orderBy("sortIndex", "asc"),
        )
        .orderBy("time", "asc"),
    );
  };
  type RawInfo = NonNullable<QueryRowType<typeof withRelated>>;

  export function asInfo(raw: RawInfo) {
    return {
      ...raw,
      dateStart: raw.dateStart,
      dateEnd: raw.dateEnd,
      slots: raw.slots.map((slot) => ({
        ...slot,
        meals: slot.meals.map((meal) => ({
          ...meal,
          items: Food.asItems(meal.items),
        })),
      })),
    };
  }

  export type Info = ReturnType<typeof asInfo>;
  export type Status = Info["status"];
  export type Slot = Info["slots"][number];
  export type SlotUserNutrientTarget = Slot["userNutrientTargets"][number];
  export type Meal = Slot["meals"][number];
  export type MealItem = Meal["items"][number];
  export type MealItemDescriptor = Meal["itemDescriptors"][number];
  export type MealOccurrence = Meal["occurrences"][number];

  export type PublishViolationTarget =
    | { entity: "slot"; id: string; code: "emptyName" | "noMeals" }
    | { entity: "meal"; id: string; code: "emptyName" | "noItems" | "hasDescriptors" }
    | { entity: "mealPlan"; code: "noSlots" };

  export type PublishViolations = {
    mealPlan: {
      noSlots?: true;
    };
    slots: Record<
      string,
      {
        emptyName?: true;
        noMeals?: true;
      }
    >;
    meals: Record<
      string,
      {
        emptyName?: true;
        noItems?: true;
        hasDescriptors?: true;
      }
    >;
    firstTarget: PublishViolationTarget;
  };

  export function publishViolations(mealPlan: Info): PublishViolations | null {
    const violations: Omit<PublishViolations, "firstTarget"> = {
      mealPlan: {},
      slots: {},
      meals: {},
    };
    let firstTarget: PublishViolationTarget | undefined = undefined;

    const setFirstTarget = (target: PublishViolationTarget) => {
      firstTarget ??= target;
    };

    if (mealPlan.slots.length === 0) {
      violations.mealPlan.noSlots = true;
      setFirstTarget({ entity: "mealPlan", code: "noSlots" });
    }

    for (const slot of mealPlan.slots) {
      const slotViolations: PublishViolations["slots"][string] = {};
      const trimmedSlotName = slot.name.trim();
      if (!trimmedSlotName) {
        slotViolations.emptyName = true;
        setFirstTarget({ entity: "slot", id: slot.id, code: "emptyName" });
      }
      if (slot.meals.length === 0) {
        slotViolations.noMeals = true;
        setFirstTarget({ entity: "slot", id: slot.id, code: "noMeals" });
      }
      if (Object.keys(slotViolations).length > 0) {
        violations.slots[slot.id] = slotViolations;
      }

      for (const meal of slot.meals) {
        const mealViolations: PublishViolations["meals"][string] = {};
        const trimmedMealName = meal.name.trim();
        if (!trimmedMealName) {
          mealViolations.emptyName = true;
          setFirstTarget({ entity: "meal", id: meal.id, code: "emptyName" });
        }
        if (meal.items.length === 0) {
          mealViolations.noItems = true;
          setFirstTarget({ entity: "meal", id: meal.id, code: "noItems" });
        }
        if (meal.itemDescriptors.length > 0) {
          mealViolations.hasDescriptors = true;
          setFirstTarget({ entity: "meal", id: meal.id, code: "hasDescriptors" });
        }
        if (Object.keys(mealViolations).length > 0) {
          violations.meals[meal.id] = mealViolations;
        }
      }
    }

    if (!firstTarget) {
      return null;
    }
    return {
      ...violations,
      firstTarget,
    };
  }

  export function isUnpublishBlocked(mealPlan: Info): boolean {
    return mealPlan.slots.some((slot) =>
      slot.meals.some((meal) =>
        meal.occurrences.some((occurrence) => occurrence.foodLogSourceGroup),
      ),
    );
  }

  export const BLOCKED_UNPUBLISH_MESSAGE =
    "Meal plan cannot be unpublished because one or more planned meal occurrences are already linked to a food log.";

  export function allMeals(mealPlan: {
    slots: readonly (Pick<Slot, "meals"> & { meals: readonly Meal[] })[];
  }): Meal[] {
    return mealPlan.slots.flatMap((slot) => slot.meals);
  }

  export function findMeal(
    mealPlan: { slots: readonly (Pick<Slot, "meals"> & { meals: readonly Meal[] })[] },
    mealId: string,
  ): Meal | undefined {
    return allMeals(mealPlan).find((meal) => meal.id === mealId);
  }

  export type FetchMealResult = {
    meal: Meal;
    mealSlot: Row<Schema["tables"]["plannedMealSlot"]>;
    slotNutrientTargets: SlotUserNutrientTarget | null;
  };

  async function lockMealPlan(tx: Transaction, mealPlanId: string, mode: "shared" | "exclusive") {
    await acquireAdvisoryLockIfServer(tx, "mealPlan", mealPlanId, mode);
  }

  export async function fetchMeal(
    tx: Transaction,
    mealPlanId: string,
    mealId: string,
  ): Promise<FetchMealResult | null> {
    const meal = await tx.run(
      zql.plannedMeal
        .where("mealPlanId", "=", mealPlanId)
        .where("id", "=", mealId)
        .related("items", (q) =>
          q
            .related("serving", (serving) =>
              serving.related("food", (food) => food.related("meta")),
            )
            .orderBy("sortIndex", "asc"),
        )
        .related("itemDescriptors", (q) => q.orderBy("sortIndex", "asc"))
        .related("occurrences", (q) => q.related("foodLogSourceGroup"))
        .related("slot", (slot) => slot.related("userNutrientTargets"))
        .one(),
    );

    if (!meal?.slot) {
      return null;
    }

    const { slot: _slot, ...mealWithoutSlot } = meal;

    const parsedMeal: Meal = {
      ...mealWithoutSlot,
      items: Food.asItems(meal.items),
    };

    return {
      meal: parsedMeal,
      mealSlot: meal.slot,
      slotNutrientTargets:
        meal.slot.userNutrientTargets.find((target) => target.userId === parsedMeal.userId) ?? null,
    };
  }

  function assertNoDuplicateFoodServings(items: readonly { foodServingId: string }[]) {
    const seenServingIds = new Set<string>();
    for (const item of items) {
      if (seenServingIds.has(item.foodServingId)) {
        throw new Error("Meal already contains this food serving");
      }

      seenServingIds.add(item.foodServingId);
    }
  }

  export const byId = defineQuery(z.object({ id: z.uuid() }), ({ ctx, args: { id } }) => {
    return withRelated(
      zql.mealPlan.where((e) => viewable(e, ctx.actor)).where("id", "=", id),
    ).one();
  });

  /**
   * @deprecated Use MealPlan.byId.fn instead
   */
  export async function getInfoById(tx: Transaction, id: string): Promise<Info | null> {
    const mealPlan = await tx.run(withRelated(zql.mealPlan.where("id", "=", id)).one());
    return mealPlan ? asInfo(mealPlan) : null;
  }

  export const forUser = defineQuery(
    z.object({ userId: z.uuid() }),
    ({ ctx, args: { userId } }) => {
      return zql.mealPlan.where((e) => viewable(e, ctx.actor)).where("ownerId", "=", userId);
    },
  );
  export type Preview = QueryRowType<typeof forUser>;

  export const publishedInDateRange = defineQuery(
    z.object({
      userId: z.uuid(),
      dateRange: DateRange.closedSchema,
    }),
    ({ ctx, args: { userId, dateRange } }) => {
      return withRelated(
        zql.mealPlan
          .where("ownerId", "=", userId)
          .where((plan) => viewable(plan, ctx.actor))
          .where("status", "=", "published")
          .where((plan) => DateRange.overlapsWhere(plan, dateRange)),
        userId,
      );
    },
  );

  export const foodLogSourceAdapter: FoodLog.SourceAdapter = {
    buildEntries: async (tx, actor, sourceId, userId, entryIds) => {
      const mealPlan = await tx.run(
        zql.plannedMealOccurrence
          .where("id", "=", sourceId)
          .related("meal", (meal) => meal.related("mealPlan").related("items"))
          .one(),
      );
      const occurrence = mealPlan;
      if (!occurrence?.meal) {
        accessDenied("Cannot view planned meal occurrence");
      }

      const viewablePlan = await tx.run(
        zql.mealPlan
          .where("id", "=", occurrence.mealPlanId)
          .where((plan) => MealPlan.viewable(plan, actor))
          .one(),
      );
      if (!viewablePlan) {
        accessDenied("Cannot view planned meal occurrence");
      }

      if (occurrence.userId !== actor.userId) {
        accessDenied("Cannot create a source-backed food log for a different user");
      }

      if (occurrence.userId !== userId) {
        accessDenied("Cannot create a source-backed food log for a different user");
      }

      if (viewablePlan.status !== "published") {
        accessDenied("Cannot create a source-backed food log from an unpublished meal plan");
      }

      if (entryIds.length !== occurrence.meal.items.length) {
        accessDenied(
          "Cannot create a source-backed food log with a different number of entries than the meal plan occurrence",
        );
      }

      return occurrence.meal.items.map((item, index) => ({
        id: must(entryIds[index]),
        foodServingId: item.foodServingId,
        amount: item.amount,
      }));
    },
  };

  export function foodLogSourceGroupContribution(
    mealPlan: Info | undefined,
    date: Temporal.PlainDate,
    timeZone: string,
  ): FoodLog.SourceGroupContribution {
    const defaultLabel = "Planned meal";
    if (!mealPlan) {
      return {
        candidates: [],
        sources: new Map(),
        defaultLabel,
      };
    }

    const sources = new Map<string, FoodLog.Source>();
    const candidates: FoodLog.SourceGroupCandidateLike[] = [];

    for (const slot of mealPlan.slots) {
      let matches: {
        meal: Meal;
        occurrence: MealOccurrence;
      }[] = [];
      let existingSourceFound = false;

      for (const meal of slot.meals) {
        let occurrenceOnDateFound = false;
        let selectedOccurrence: MealOccurrence | null = null;
        for (const occurrence of meal.occurrences) {
          if (occurrence.foodLogSourceGroup) {
            const sourceDate = occurrence.foodLogSourceGroup.timeAt
              .toZonedDateTimeISO(timeZone)
              .toPlainDate();
            if (sourceDate.equals(date)) {
              sources.set(occurrence.id, {
                label: `${slot.name} - ${meal.name}`,
                items: meal.items,
                status: "resolved",
              });
              existingSourceFound = true;
              break;
            } else {
              continue;
            }
          }

          const occurrenceDate = occurrence.date;
          if (occurrenceDate === null) {
            selectedOccurrence ??= occurrence;
          } else if (occurrenceDate.equals(date)) {
            selectedOccurrence = occurrence;
            occurrenceOnDateFound = true;
            break;
          }
        }

        if (selectedOccurrence && !existingSourceFound) {
          matches.push({
            meal,
            occurrence: selectedOccurrence,
          });
        }

        if (occurrenceOnDateFound || existingSourceFound) {
          break;
        }
      }

      if (existingSourceFound) {
        continue;
      }

      const timeAt = TemporalHelpers.zonedDateTime({
        date,
        timeZone,
        time: slot.time,
      }).toInstant();

      if (matches.length === 1) {
        const { meal, occurrence } = must(matches[0]);
        candidates.push({
          kind: "sourceGroupCandidate",
          sourceType: "planned_meal_occurrence",
          id: slot.id,
          timeAt,
          label: `${slot.name} - ${meal.name}`,
          items: meal.items,
          sourceId: occurrence.id,
        });
      } else if (matches.length > 1) {
        candidates.push({
          kind: "sourceGroupCandidateGroup",
          id: slot.id,
          timeAt: TemporalHelpers.zonedDateTime({
            date,
            timeZone,
            time: slot.time,
          }).toInstant(),
          label: `${slot.name}`,
          candidates: matches.map(({ meal, occurrence }) => ({
            kind: "sourceGroupCandidate",
            id: meal.id,
            timeAt,
            sourceType: "planned_meal_occurrence",
            label: `${meal.name}`,
            items: meal.items,
            sourceId: occurrence.id,
          })),
        });
      }
    }

    return { candidates, sources, defaultLabel };
  }

  export function viewable(eb: ExpressionBuilder<"mealPlan", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("ownerId", "=", actor.userId),
      eb.exists("members", (q) => q.where("userId", "=", actor.userId)),
    );
  }

  export async function canView(
    tx: Transaction,
    actor: UserActor,
    mealPlanId: string,
  ): Promise<boolean> {
    const result = await tx.run(
      zql.mealPlan
        .where("id", "=", mealPlanId)
        .where((e) => viewable(e, actor))
        .one(),
    );
    return !!result;
  }

  function isOwner(actor: UserActor, mealPlan: { ownerId: string }): boolean {
    return actor.userId === mealPlan.ownerId;
  }

  const removeByIdSchema = z.object({ id: z.uuid() });

  const userNutrientTargetSchemas = {
    set: z.object({
      userId: z.uuid(),
      nutrientTargets: Nutrient.targetsSchema(),
    }),
    remove: z.object({ userId: z.uuid() }),
  };

  const mealItemSchemas = {
    add: createInsertSchema(plannedMealItem).omit({ mealId: true, mealPlanId: true }),
    edit: createUpdateSchema(plannedMealItem).omit({ mealId: true, mealPlanId: true }).extend({
      id: z.uuid(),
    }),
  };

  const mealItemDescriptorSchemas = {
    add: createInsertSchema(plannedMealItemDescriptor)
      .omit({ mealPlanId: true, mealId: true })
      .extend({
        data: FoodItemMatcher.ItemDescriptor,
      }),
    edit: createUpdateSchema(plannedMealItemDescriptor)
      .omit({ mealPlanId: true, mealId: true })
      .extend({
        id: z.uuid(),
        data: FoodItemMatcher.ItemDescriptor.optional(),
      }),
  };

  const mealOccurrenceSchemas = {
    add: createInsertSchema(plannedMealOccurrence)
      .omit({ mealPlanId: true, mealId: true, mealSlotId: true, userId: true })
      .extend({ date: temporalPlainDateSchema.nullable() }),
    edit: createUpdateSchema(plannedMealOccurrence)
      .omit({ mealPlanId: true, mealId: true, mealSlotId: true, userId: true })
      .extend({
        id: z.uuid(),
        date: temporalPlainDateSchema.nullable().optional(),
      }),
  };

  const mealSchemas = {
    add: createInsertSchema(plannedMeal).omit({ mealPlanId: true, mealSlotId: true }).extend({
      items: mealItemSchemas.add.array(),
      itemDescriptors: mealItemDescriptorSchemas.add.array(),
      occurrences: mealOccurrenceSchemas.add.array(),
    }),
    edit: createUpdateSchema(plannedMeal)
      .omit({ mealPlanId: true, mealSlotId: true, userId: true })
      .extend({
        id: z.uuid(),
        items: z
          .object({
            add: mealItemSchemas.add.array().optional(),
            edit: mealItemSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
        itemDescriptors: z
          .object({
            add: mealItemDescriptorSchemas.add.array().optional(),
            edit: mealItemDescriptorSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
        occurrences: z
          .object({
            add: mealOccurrenceSchemas.add.array().optional(),
            edit: mealOccurrenceSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
      }),
  };

  export const addMealSchema = mealSchemas.add.extend({
    mealPlanId: z.uuid(),
    mealSlotId: z.uuid(),
  });
  export type AddMeal = z.infer<typeof addMealSchema>;

  const mealSlotSchemas = {
    add: createInsertSchema(plannedMealSlot).omit({ mealPlanId: true }).extend({
      time: temporalPlainTimeSchema,
      userNutrientTargets: userNutrientTargetSchemas.set.array(),
      meals: mealSchemas.add.array(),
    }),
    edit: createUpdateSchema(plannedMealSlot)
      .omit({ mealPlanId: true })
      .extend({
        id: z.uuid(),
        time: temporalPlainTimeSchema.optional(),
        userNutrientTargets: z
          .object({
            set: userNutrientTargetSchemas.set.array().optional(),
            remove: userNutrientTargetSchemas.remove.array().optional(),
          })
          .optional(),
        meals: z
          .object({
            add: mealSchemas.add.array().optional(),
            edit: mealSchemas.edit.array().optional(),
            remove: removeByIdSchema.array().optional(),
          })
          .optional(),
      }),
  };

  const mealPlanSchemas = {
    create: createInsertSchema(mealPlan)
      .omit({ status: true, dateStart: true, dateEnd: true })
      .extend({
        slots: mealSlotSchemas.add.array(),
        dateRange: DateRange.closedSchema,
      }),
    edit: z.object({
      id: z.uuid(),
      dateRange: DateRange.closedSchema.optional(),
      slots: z
        .object({
          add: mealSlotSchemas.add.array().optional(),
          edit: mealSlotSchemas.edit.array().optional(),
          remove: removeByIdSchema.array().optional(),
        })
        .optional(),
    }),
  };

  export const addMealSlotSchema = mealSlotSchemas.add.extend({ mealPlanId: z.uuid() });
  export type AddMealSlot = z.infer<typeof addMealSlotSchema>;
  export const addMeal = withZeroTransaction(addMealSchema, async ({ tx, ctx, args }) => {
    await lockMealPlan(tx, args.mealPlanId, "shared");
    const canEditResult = await canEdit(tx, ctx, args.mealPlanId);
    if (canEditResult.value === false) {
      accessDenied("Cannot add meal to meal plan");
    }

    await assertValidReferencesOnMeal(tx, ctx.actor, args);
    const timeNow = Temporal.Now.instant();

    await addMealInternal(tx, args, timeNow);
  });

  async function addMealInternal(
    tx: Transaction,
    { items, itemDescriptors, occurrences, ...args }: AddMeal,
    timeNow: Temporal.Instant,
  ) {
    await tx.mutate.plannedMeal.insert({
      ...args,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });

    await Promise.all(
      items.map(async (item) => {
        await tx.mutate.plannedMealItem.insert({
          ...item,
          mealId: args.id,
          mealPlanId: args.mealPlanId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });
      }),
    );

    await Promise.all(
      itemDescriptors.map(async (descriptor) => {
        await tx.mutate.plannedMealItemDescriptor.insert({
          ...descriptor,
          mealId: args.id,
          mealPlanId: args.mealPlanId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });
      }),
    );

    await Promise.all(
      occurrences.map(async ({ date, ...occurrence }) =>
        tx.mutate.plannedMealOccurrence.insert({
          ...occurrence,
          date,
          mealId: args.id,
          mealPlanId: args.mealPlanId,
          mealSlotId: args.mealSlotId,
          userId: args.userId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );
  }

  export const addMealSlot = withZeroTransaction(addMealSlotSchema, async ({ tx, ctx, args }) => {
    await lockMealPlan(tx, args.mealPlanId, "shared");
    const canEditResult = await canEdit(tx, ctx, args.mealPlanId);
    if (canEditResult.value === false) {
      accessDenied("Cannot add meal slot to meal plan");
    }

    await Promise.all(args.meals.map((meal) => assertValidReferencesOnMeal(tx, ctx.actor, meal)));
    const timeNow = Temporal.Now.instant();
    await addMealSlotInternal(tx, args, timeNow);
  });

  async function addMealSlotInternal(
    tx: Transaction,
    { userNutrientTargets, meals, time, ...input }: AddMealSlot,
    timeNow: Temporal.Instant,
  ) {
    await tx.mutate.plannedMealSlot.insert({
      ...input,
      time,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await Promise.all(
      userNutrientTargets.map(async (userNutrientTarget) =>
        tx.mutate.plannedMealNutrientTargets.insert({
          ...userNutrientTarget,
          nutrientTargets: Nutrient.normalizeTargets(userNutrientTarget.nutrientTargets),
          timeCreated: timeNow,
          timeUpdated: timeNow,
          mealPlanId: input.mealPlanId,
          mealSlotId: input.id,
        }),
      ),
    );

    await Promise.all(
      meals.map(async (meal) => {
        await addMealInternal(
          tx,
          {
            ...meal,
            mealPlanId: input.mealPlanId,
            mealSlotId: input.id,
          },
          timeNow,
        );
      }),
    );
  }

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    mealPlanSchemas.create,
    async ({ tx, ctx: { actor }, args: { dateRange, slots, ...input } }) => {
      must(actor.userId === input.ownerId, "You can only create meal plans for yourself");

      await Promise.all(
        slots.flatMap((slot) =>
          slot.meals.map(async (meal) => {
            await assertValidReferencesOnMeal(tx, actor, meal);
          }),
        ),
      );

      const { dateStart, dateEnd } = dateRange;
      const timeNow = Temporal.Now.instant();

      await tx.mutate.mealPlan.insert({
        ...input,
        timeCreated: timeNow,
        timeUpdated: timeNow,
        dateStart,
        dateEnd,
        status: "draft",
      });

      await tx.mutate.mealPlanMember.insert({
        mealPlanId: input.id,
        userId: input.ownerId,
        timeCreated: timeNow,
      });

      await Promise.all(
        slots.map(async (slot) => {
          await addMealSlotInternal(tx, { ...slot, mealPlanId: input.id }, timeNow);
        }),
      );
    },
  );

  async function assertValidReferencesOnMeal(
    tx: Transaction,
    actor: UserActor,
    { items }: { items: readonly { foodServingId: string }[] },
  ) {
    assertNoDuplicateFoodServings(items);

    const servings = await tx.run(
      zql.foodServing.where("id", "IN", [...new Set(items.map((entry) => entry.foodServingId))]),
    );
    const foodIds = servings.map((serving) => serving.foodId);
    const canViewFoods = await Food.canViewBatch(tx, actor, foodIds);
    if (!canViewFoods) {
      accessDenied("Cannot view food on meal item");
    }
  }

  function mapById<T extends { id: string }>(items: readonly T[]) {
    return new Map(items.map((item) => [item.id, item]));
  }

  async function setMealSlotUserNutrientTargets(
    tx: Transaction,
    slot: Slot,
    targets: readonly z.infer<typeof userNutrientTargetSchemas.set>[],
    timeNow: Temporal.Instant,
  ) {
    for (const targetSet of targets) {
      const existingTarget = slot.userNutrientTargets.find(
        (candidate) => candidate.userId === targetSet.userId,
      );

      if (existingTarget) {
        await tx.mutate.plannedMealNutrientTargets.update({
          mealPlanId: slot.mealPlanId,
          mealSlotId: slot.id,
          userId: targetSet.userId,
          nutrientTargets: Nutrient.normalizeTargets(targetSet.nutrientTargets),
          timeUpdated: timeNow,
        });
      } else {
        await tx.mutate.plannedMealNutrientTargets.insert({
          mealPlanId: slot.mealPlanId,
          mealSlotId: slot.id,
          userId: targetSet.userId,
          nutrientTargets: Nutrient.normalizeTargets(targetSet.nutrientTargets),
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });
      }
    }
  }

  async function removeMealSlotUserNutrientTargets(
    tx: Transaction,
    slot: Slot,
    removals: readonly z.infer<typeof userNutrientTargetSchemas.remove>[],
  ) {
    for (const { userId } of removals) {
      const target = slot.userNutrientTargets.find((candidate) => candidate.userId === userId);
      if (!target) {
        accessDenied("Slot nutrient target not found on meal plan");
      }
      await tx.mutate.plannedMealNutrientTargets.delete(target);
    }
  }

  export async function canEdit(
    tx: Transaction,
    ctx: Context,
    mealPlanId: string,
    opts?: { skipStatusCheck?: boolean },
  ): Promise<{ value: false } | { value: boolean; mealPlan: Info }> {
    const mealPlan = await tx.run(
      byId.fn({
        ctx,
        args: { id: mealPlanId },
      }),
    );
    if (!mealPlan) {
      return { value: false };
    }

    if (!opts?.skipStatusCheck && mealPlan.status !== "draft") {
      return { value: false };
    }
    return {
      value: isOwner(ctx.actor, mealPlan),
      mealPlan: asInfo(mealPlan),
    };
  }

  export type RemoveMealSlot = z.infer<typeof removeMealSlot.schema>;
  export const removeMealSlot = withZeroTransaction(
    z.object({ id: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { id, mealPlanId } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal slot from meal plan");
      }
      const { mealPlan } = canEditResult;
      const slot = mealPlan.slots.find((slot) => slot.id === id);
      if (!slot) {
        accessDenied("Meal slot not found on meal plan");
      }

      await removeMealSlotInternal(tx, slot);
    },
  );

  export type RemoveMeal = z.infer<typeof removeMeal.schema>;
  export const removeMeal = withZeroTransaction(
    z.object({ id: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { id, mealPlanId } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal from meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, id);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }

      await removeMealInternal(tx, meal);
    },
  );

  async function removeMealInternal(tx: Transaction, meal: Meal) {
    const workflowExecutions = await tx.run(
      zql.mealPlanMealWorkflowExecution
        .where("mealPlanId", "=", meal.mealPlanId)
        .where("mealId", "=", meal.id),
    );

    await Promise.all(
      workflowExecutions.map((execution) =>
        tx.mutate.mealPlanMealWorkflowExecution.delete(execution),
      ),
    );
    await Promise.all(meal.items.map(({ id }) => tx.mutate.plannedMealItem.delete({ id })));
    await Promise.all(
      meal.itemDescriptors.map(({ id }) => tx.mutate.plannedMealItemDescriptor.delete({ id })),
    );
    await Promise.all(
      meal.occurrences.map(({ id }) => tx.mutate.plannedMealOccurrence.delete({ id })),
    );
    await tx.mutate.plannedMeal.delete({ id: meal.id });
  }

  export type AddMealItem = z.infer<typeof addMealItem.schema>;
  export const addMealItem = withZeroTransaction(
    mealItemSchemas.add.extend({ mealId: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, mealPlanId, ...item } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot add meal item to meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }

      const canViewResult = await Food.canViewByServingId(tx, ctx.actor, item.foodServingId);
      if (!canViewResult) {
        accessDenied("Cannot view food");
      }

      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealItem.insert({
        ...item,
        mealId,
        mealPlanId,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );

  export type AddMealItemDescriptor = z.infer<typeof addMealItemDescriptor.schema>;
  export const addMealItemDescriptor = withZeroTransaction(
    mealItemDescriptorSchemas.add.extend({ mealId: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, mealPlanId, ...item } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot add meal item descriptor to meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealItemDescriptor.insert({
        ...item,
        mealId,
        mealPlanId,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );

  export type AddMealOccurrence = z.infer<typeof addMealOccurrence.schema>;
  export const addMealOccurrence = withZeroTransaction(
    mealOccurrenceSchemas.add.extend({ mealId: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, mealPlanId, date, ...occurrence } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot add meal occurrence to meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealOccurrence.insert({
        ...occurrence,
        date,
        mealId,
        mealPlanId,
        mealSlotId: meal.mealSlotId,
        userId: meal.userId,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );

  export type SetMealUserNutrientTargets = z.infer<typeof setMealUserNutrientTargets.schema>;
  export const setMealUserNutrientTargets = withZeroTransaction(
    userNutrientTargetSchemas.set.extend({
      mealSlotId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { userId, mealSlotId, mealPlanId, nutrientTargets } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal user nutrient target on meal plan");
      }
      const { mealPlan } = canEditResult;
      const slot = mealPlan.slots.find((s) => s.id === mealSlotId);
      if (!slot) {
        accessDenied("Slot not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await setMealSlotUserNutrientTargets(tx, slot, [{ userId, nutrientTargets }], timeNow);
    },
  );

  export type RemoveMealUserNutrientTargets = z.infer<typeof removeMealUserNutrientTargets.schema>;
  export const removeMealUserNutrientTargets = withZeroTransaction(
    userNutrientTargetSchemas.remove.extend({
      mealSlotId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { userId, mealSlotId, mealPlanId } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal user nutrient target on meal plan");
      }
      const { mealPlan } = canEditResult;
      const slot = mealPlan.slots.find((s) => s.id === mealSlotId);
      if (!slot) {
        accessDenied("Slot not found on meal plan");
      }

      await removeMealSlotUserNutrientTargets(tx, slot, [{ userId }]);
    },
  );

  export type EditMealItem = z.infer<typeof editMealItem.schema>;
  export const editMealItem = withZeroTransaction(
    mealItemSchemas.edit.extend({
      mealId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { mealId, mealPlanId, ...input } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal item on meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const item = meal.items.find((candidate) => candidate.id === input.id);
      if (!item) {
        accessDenied("Item not found on meal plan");
      }
      if (input.foodServingId && input.foodServingId !== item.foodServingId) {
        const canViewResult = await Food.canViewByServingId(tx, ctx.actor, input.foodServingId);
        if (!canViewResult) {
          accessDenied("Cannot view food");
        }
      }
      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealItem.update({
        ...input,
        timeUpdated: timeNow,
      });
    },
  );

  export type EditMealItemDescriptor = z.infer<typeof editMealItemDescriptor.schema>;
  export const editMealItemDescriptor = withZeroTransaction(
    mealItemDescriptorSchemas.edit.extend({
      mealId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { mealId, mealPlanId, ...input } }) => {
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal item descriptor on meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const descriptor = meal.itemDescriptors.find((i) => i.id === input.id);
      if (!descriptor) {
        accessDenied("Item descriptor not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealItemDescriptor.update({
        ...input,
        timeUpdated: timeNow,
      });
    },
  );

  export type EditMealOccurrence = z.infer<typeof editMealOccurrence.schema>;
  export const editMealOccurrence = withZeroTransaction(
    mealOccurrenceSchemas.edit.extend({
      mealId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { mealId, mealPlanId, date, ...input } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal occurrence on meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const occurrence = meal.occurrences.find((candidate) => candidate.id === input.id);
      if (!occurrence) {
        accessDenied("Occurrence not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await tx.mutate.plannedMealOccurrence.update({
        ...input,
        date,
        timeUpdated: timeNow,
      });
    },
  );

  export type RemoveMealItem = z.infer<typeof removeMealItem.schema>;
  export const removeMealItem = withZeroTransaction(
    z.object({ id: z.uuid(), mealId: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, mealPlanId, id } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal item from meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const item = meal.items.find((candidate) => candidate.id === id);
      if (!item) {
        accessDenied("Item not found on meal plan");
      }

      await tx.mutate.plannedMealItem.delete(item);
    },
  );

  export type RemoveMealItemDescriptor = z.infer<typeof removeMealItemDescriptor.schema>;
  export const removeMealItemDescriptor = withZeroTransaction(
    z.object({ id: z.uuid(), mealId: z.uuid(), mealPlanId: z.uuid() }),
    async ({ tx, ctx, args: { mealId, mealPlanId, id } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal item descriptor from meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const descriptor = meal.itemDescriptors.find((candidate) => candidate.id === id);
      if (!descriptor) {
        accessDenied("Item descriptor not found on meal plan");
      }

      await tx.mutate.plannedMealItemDescriptor.delete(descriptor);
    },
  );

  export type RemoveMealOccurrence = z.infer<typeof removeMealOccurrence.schema>;
  export const removeMealOccurrence = withZeroTransaction(
    z.object({
      id: z.uuid(),
      mealId: z.uuid(),
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { mealId, mealPlanId, id } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot remove meal occurrence from meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, mealId);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const occurrence = meal.occurrences.find((candidate) => candidate.id === id);
      if (!occurrence) {
        accessDenied("Occurrence not found on meal plan");
      }

      await tx.mutate.plannedMealOccurrence.delete(occurrence);
    },
  );

  export type EditMealSlot = z.infer<typeof editMealSlot.schema>;
  export const editMealSlot = withZeroTransaction(
    mealSlotSchemas.edit.extend({
      mealPlanId: z.uuid(),
    }),
    async ({ tx, ctx, args: { mealPlanId, ...input } }) => {
      await lockMealPlan(tx, mealPlanId, "shared");
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal slot on meal plan");
      }
      const { mealPlan } = canEditResult;
      const slot = mealPlan.slots.find((candidate) => candidate.id === input.id);
      if (!slot) {
        accessDenied("Slot not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await editMealSlotInternal(tx, ctx.actor, slot, input, timeNow);
    },
  );

  export type EditMeal = z.infer<typeof editMeal.schema>;
  export const editMeal = withZeroTransaction(
    mealSchemas.edit.extend({ mealPlanId: z.uuid() }),
    async ({ tx, ctx, args }) => {
      await lockMealPlan(tx, args.mealPlanId, "shared");
      const { mealPlanId, items, itemDescriptors, occurrences, ...input } = args;
      const canEditResult = await canEdit(tx, ctx, mealPlanId);
      if (canEditResult.value === false) {
        accessDenied("Cannot edit meal on meal plan");
      }
      const { mealPlan } = canEditResult;
      const meal = findMeal(mealPlan, input.id);
      if (!meal) {
        accessDenied("Meal not found on meal plan");
      }
      const timeNow = Temporal.Now.instant();

      await editMealInternal(
        tx,
        ctx.actor,
        meal,
        {
          ...input,
          items,
          itemDescriptors,
          occurrences,
        },
        timeNow,
      );
    },
  );

  export type Edit = z.infer<typeof edit.schema>;
  export const edit = withZeroTransaction(mealPlanSchemas.edit, async ({ tx, ctx, args }) => {
    await lockMealPlan(tx, args.id, "shared");
    const canEditResult = await canEdit(tx, ctx, args.id);
    if (canEditResult.value === false) {
      accessDenied("Cannot edit meal plan");
    }
    const { mealPlan } = canEditResult;

    const timeNow = Temporal.Now.instant();

    assertValidIdChanges(mealPlan.slots, args.slots, {
      operation: "MealPlan.edit",
      label: "slot",
    });

    if (args.dateRange) {
      await tx.mutate.mealPlan.update({
        id: args.id,
        dateStart: args.dateRange.dateStart,
        dateEnd: args.dateRange.dateEnd,
        timeUpdated: timeNow,
      });
    }

    const slotRemovals = args.slots?.remove ?? [];
    const slotsById = mapById(mealPlan.slots);
    await Promise.all(
      slotRemovals.map(({ id }) => removeMealSlotInternal(tx, must(slotsById.get(id)))),
    );

    const slotEdits = args.slots?.edit ?? [];
    await Promise.all(
      slotEdits.map((slotEdit) =>
        editMealSlotInternal(tx, ctx.actor, must(slotsById.get(slotEdit.id)), slotEdit, timeNow),
      ),
    );

    const slotAdds = args.slots?.add ?? [];
    await Promise.all(
      slotAdds.map(async (slotAdd) => {
        await Promise.all(
          slotAdd.meals.map((meal) => assertValidReferencesOnMeal(tx, ctx.actor, meal)),
        );
        await addMealSlotInternal(tx, { ...slotAdd, mealPlanId: args.id }, timeNow);
      }),
    );
  });

  async function editMealInternal(
    tx: Transaction,
    actor: UserActor,
    meal: Meal,
    args: Omit<EditMeal, "mealPlanId">,
    timeNow: Temporal.Instant,
  ) {
    const { items, itemDescriptors, occurrences, ...mealUpdate } = args;

    assertValidIdChanges(meal.items, items, {
      operation: "MealPlan.edit",
      label: "meal item",
    });
    assertValidIdChanges(meal.itemDescriptors, itemDescriptors, {
      operation: "MealPlan.edit",
      label: "meal item descriptor",
    });
    assertValidIdChanges(meal.occurrences, occurrences, {
      operation: "MealPlan.edit",
      label: "meal occurrence",
    });

    const itemReferenceCandidates = [
      ...(items?.add ?? []),
      ...(items?.edit ?? []).flatMap((item) =>
        item.foodServingId ? [{ foodServingId: item.foodServingId }] : [],
      ),
    ];
    if (itemReferenceCandidates.length > 0) {
      await assertValidReferencesOnMeal(tx, actor, {
        items: itemReferenceCandidates,
      });
    }

    await tx.mutate.plannedMeal.update({
      ...mealUpdate,
      id: meal.id,
      timeUpdated: timeNow,
    });

    await Promise.all(
      (items?.remove ?? []).map(({ id }) => tx.mutate.plannedMealItem.delete({ id })),
    );
    await Promise.all(
      (items?.edit ?? []).map((itemEdit) =>
        tx.mutate.plannedMealItem.update({
          ...itemEdit,
          timeUpdated: timeNow,
        }),
      ),
    );
    await Promise.all(
      (items?.add ?? []).map((itemAdd) =>
        tx.mutate.plannedMealItem.insert({
          ...itemAdd,
          mealId: meal.id,
          mealPlanId: meal.mealPlanId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );

    await Promise.all(
      (itemDescriptors?.remove ?? []).map(({ id }) =>
        tx.mutate.plannedMealItemDescriptor.delete({ id }),
      ),
    );
    await Promise.all(
      (itemDescriptors?.edit ?? []).map((descriptorEdit) =>
        tx.mutate.plannedMealItemDescriptor.update({
          ...descriptorEdit,
          timeUpdated: timeNow,
        }),
      ),
    );
    await Promise.all(
      (itemDescriptors?.add ?? []).map((descriptorAdd) =>
        tx.mutate.plannedMealItemDescriptor.insert({
          ...descriptorAdd,
          mealId: meal.id,
          mealPlanId: meal.mealPlanId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );

    await Promise.all(
      (occurrences?.remove ?? []).map(({ id }) => tx.mutate.plannedMealOccurrence.delete({ id })),
    );
    await Promise.all(
      (occurrences?.edit ?? []).map((occurrenceEdit) =>
        tx.mutate.plannedMealOccurrence.update({
          ...occurrenceEdit,
          timeUpdated: timeNow,
        }),
      ),
    );
    await Promise.all(
      (occurrences?.add ?? []).map((occurrenceAdd) =>
        tx.mutate.plannedMealOccurrence.insert({
          ...occurrenceAdd,
          mealId: meal.id,
          mealPlanId: meal.mealPlanId,
          mealSlotId: meal.mealSlotId,
          userId: meal.userId,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        }),
      ),
    );
  }

  async function removeMealSlotInternal(tx: Transaction, slot: Slot) {
    await Promise.all(slot.meals.map((meal) => removeMealInternal(tx, meal)));
    await Promise.all(
      slot.userNutrientTargets.map((target) => tx.mutate.plannedMealNutrientTargets.delete(target)),
    );
    await tx.mutate.plannedMealSlot.delete({ id: slot.id });
  }

  async function editMealSlotInternal(
    tx: Transaction,
    actor: UserActor,
    slot: Slot,
    args: z.infer<typeof mealSlotSchemas.edit>,
    timeNow: Temporal.Instant,
  ) {
    const { userNutrientTargets, meals, ...slotUpdate } = args;

    assertValidIdChanges(slot.meals, meals, {
      operation: "MealPlan.edit",
      label: "meal",
    });

    if (Object.keys(slotUpdate).some((key) => key !== "id")) {
      await tx.mutate.plannedMealSlot.update({
        ...slotUpdate,
        timeUpdated: timeNow,
      });
    }

    await setMealSlotUserNutrientTargets(tx, slot, userNutrientTargets?.set ?? [], timeNow);
    await removeMealSlotUserNutrientTargets(tx, slot, userNutrientTargets?.remove ?? []);

    const mealsById = mapById(slot.meals);

    await Promise.all(
      (meals?.remove ?? []).map(({ id }) => removeMealInternal(tx, must(mealsById.get(id)))),
    );
    await Promise.all(
      (meals?.edit ?? []).map((mealEdit) =>
        editMealInternal(tx, actor, must(mealsById.get(mealEdit.id)), mealEdit, timeNow),
      ),
    );
    await Promise.all(
      (meals?.add ?? []).map(async (mealAdd) => {
        await assertValidReferencesOnMeal(tx, actor, mealAdd);
        await addMealInternal(
          tx,
          {
            ...mealAdd,
            mealPlanId: slot.mealPlanId,
            mealSlotId: slot.id,
          },
          timeNow,
        );
      }),
    );
  }

  export type Publish = z.infer<typeof publish.schema>;
  export const publish = withZeroTransaction(
    z.object({
      id: z.uuid(),
    }),
    async ({ tx, ctx, args: { id } }) => {
      await lockMealPlan(tx, id, "exclusive");
      const canEditResult = await canEdit(tx, ctx, id, { skipStatusCheck: true });
      if (canEditResult.value === false) {
        accessDenied("Cannot publish meal plan");
      }

      const violations = publishViolations(canEditResult.mealPlan);
      if (violations) {
        throw new Error("Meal plan is not ready to publish");
      }

      const timeNow = Temporal.Now.instant();
      await tx.mutate.mealPlan.update({
        id,
        status: "published",
        timeUpdated: timeNow,
      });
    },
  );

  export type Unpublish = z.infer<typeof unpublish.schema>;
  export const unpublish = withZeroTransaction(
    z.object({
      id: z.uuid(),
    }),
    async ({ tx, ctx, args: { id } }) => {
      await lockMealPlan(tx, id, "exclusive");
      const canEditResult = await canEdit(tx, ctx, id, { skipStatusCheck: true });
      if (canEditResult.value === false) {
        accessDenied("Cannot unpublish meal plan");
      }

      if (isUnpublishBlocked(canEditResult.mealPlan)) {
        throw new Error(BLOCKED_UNPUBLISH_MESSAGE);
      }

      const timeNow = Temporal.Now.instant();
      await tx.mutate.mealPlan.update({
        id,
        status: "draft",
        timeUpdated: timeNow,
      });
    },
  );

  export type Delete = z.infer<typeof del.schema>;
  export const del = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      await lockMealPlan(tx, id, "exclusive");
      const canEditResult = await canEdit(tx, ctx, id, { skipStatusCheck: true });
      if (canEditResult.value === false) {
        accessDenied("Cannot delete meal plan");
      }
      const { mealPlan } = canEditResult;
      if (mealPlan.status !== "draft") {
        throw new Error("Only draft meal plans can be deleted. Revert to draft first.");
      }

      await Promise.all(allMeals(mealPlan).map((meal) => removeMealInternal(tx, meal)));

      await Promise.all(
        mealPlan.slots.flatMap((slot) =>
          slot.userNutrientTargets.map((target) =>
            tx.mutate.plannedMealNutrientTargets.delete(target),
          ),
        ),
      );
      await Promise.all(mealPlan.slots.map((slot) => tx.mutate.plannedMealSlot.delete(slot)));

      const members = await tx.run(zql.mealPlanMember.where("mealPlanId", "=", id));
      const invites = await tx.run(zql.mealPlanInvite.where("mealPlanId", "=", id));
      await Promise.all(members.map((member) => tx.mutate.mealPlanMember.delete(member)));
      await Promise.all(invites.map((invite) => tx.mutate.mealPlanInvite.delete(invite)));

      const links = await tx.run(zql.mealPlanAgentSession.where("mealPlanId", "=", id));
      await Promise.all(links.map((link) => tx.mutate.mealPlanAgentSession.delete(link)));

      const shoppingLists = await tx.run(zql.shoppingList.where("mealPlanId", "=", id));
      await Promise.all(
        shoppingLists.map((shoppingList) =>
          tx.mutate.shoppingList.update({ ...shoppingList, mealPlanId: null }),
        ),
      );

      await tx.mutate.mealPlan.delete(mealPlan);
    },
  );

  export async function assertSingleOwnerMember(
    tx: Transaction,
    mealPlanId: string,
    expectedOwnerId: string,
  ) {
    const allMembers = await tx.run(zql.mealPlanMember.where("mealPlanId", "=", mealPlanId));
    must(
      allMembers.length === 1 && allMembers[0]?.userId === expectedOwnerId,
      "Generation workflow currently supports single-member meal plans only",
    );
  }
}
